// Semana e Backlog — Rizoma_Manejo_Operacoes_v2_Spec.md §6.2 e §6.3.
//
// As telas de quem PLANEJA. Mesmo motivo de `manejo-hoje.ts` para morar em
// shared/: são as regras que dá para testar sem DOM (o repo não tem jsdom), e
// regra dentro de `.map()` de componente é regra que ninguém confere.
//
// A janela da semana também é calculada no servidor (`semanaParaRange` em
// server/routes/manejo/operacoes.ts, atrás de `?semana=`). As duas contas têm
// que dar o mesmo intervalo — `diasDaSemana` abaixo é a versão do client, e o
// teste cobre as duas contra os mesmos casos de borda.

import type { MnjOperacaoStatus, MnjOperacaoPrioridade } from "./schema.js";
import { somarDiasCivis } from "./data-fazenda.js";
import { grupoDaOperacao, type OperacaoOrdenavel } from "./manejo-hoje.js";

/** Prioridade → peso. `alta` primeiro; valor desconhecido cai como `normal`. */
const PESO_PRIORIDADE: Record<string, number> = { alta: 0, normal: 1, baixa: 2 };

// ─── Semana ───────────────────────────────────────────────────────────────────

export interface DiaDaSemana {
  /** YYYY-MM-DD */
  data: string;
  /** 1 = segunda … 7 = domingo (ISO). */
  diaSemana: number;
  ehHoje: boolean;
}

/**
 * A segunda-feira da semana que contém `data`.
 *
 * Conta em UTC sobre uma data civil (`YYYY-MM-DD` + `T00:00:00Z`), que é
 * aritmética de calendário pura — nada aqui depende do fuso do processo. Espelha
 * `semanaParaRange` do servidor de propósito: as duas precisam concordar, senão
 * a tela pede uma semana e recebe outra.
 */
export function segundaDaSemana(data: string): string {
  const d = new Date(data + "T00:00:00Z");
  const diaSemana = d.getUTCDay(); // 0 = domingo
  const offsetSegunda = diaSemana === 0 ? -6 : 1 - diaSemana;
  return somarDiasCivis(data, offsetSegunda);
}

/** Intervalo fechado seg→dom da semana que contém `data`. */
export function intervaloDaSemana(data: string): { inicio: string; fim: string } {
  const inicio = segundaDaSemana(data);
  return { inicio, fim: somarDiasCivis(inicio, 6) };
}

/**
 * Os dias que a grade mostra: **seg–sáb sempre; domingo só se tiver operação**.
 *
 * A fazenda não trabalha domingo — uma sétima coluna vazia rouba largura das seis
 * que importam, e no celular vira um cabeçalho a mais para rolar. Mas domingo
 * COM trabalho não pode sumir: já aconteceu de a irrigação rodar no domingo.
 */
export function diasDaSemana(data: string, temOperacaoNoDomingo: boolean): DiaDaSemana[] {
  const inicio = segundaDaSemana(data);
  const total = temOperacaoNoDomingo ? 7 : 6;
  const dias: DiaDaSemana[] = [];
  for (let i = 0; i < total; i++) {
    const d = somarDiasCivis(inicio, i);
    dias.push({ data: d, diaSemana: i + 1, ehHoje: false });
  }
  return dias;
}

/**
 * Número ISO da semana — o rótulo "Semana 35" do cabeçalho.
 *
 * ISO-8601: a semana 1 é a que contém a primeira quinta-feira do ano. Vale a
 * conta certa e não uma aproximação porque o Hugo usa esse número para falar com
 * a equipe ("o que ficou da 34?"), e um off-by-one em janeiro seria confusão real.
 */
export function semanaIsoDe(data: string): number {
  const d = new Date(data + "T00:00:00Z");
  // Anda até a quinta-feira da mesma semana: é ela que define o ano ISO.
  const diaIso = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  d.setUTCDate(d.getUTCDate() + 4 - diaIso);
  const inicioDoAno = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - inicioDoAno.getTime()) / 86400000 + 1) / 7);
}

export interface GrupoDia<T> {
  dia: DiaDaSemana;
  operacoes: T[];
}

/**
 * As operações da semana, por dia, cada dia já ordenado por prioridade.
 *
 * Concluídas e canceladas ENTRAM: a Semana é a tela de quem planeja, e "esse dia
 * já está resolvido" é informação de planejamento. É o oposto da tela Hoje, que
 * mostra só o que ainda pede ação. Backlog (sem data) nunca entra — ele tem guia
 * própria.
 */
export function agruparPorDia<T extends OperacaoOrdenavel>(
  operacoes: T[],
  dias: DiaDaSemana[],
): Array<GrupoDia<T>> {
  const porData = new Map<string, T[]>();
  for (const dia of dias) porData.set(dia.data, []);
  for (const op of operacoes) {
    if (!op.dataPrevista) continue;
    const balde = porData.get(op.dataPrevista);
    if (balde) balde.push(op);
  }
  return dias.map(dia => ({
    dia,
    operacoes: (porData.get(dia.data) ?? []).slice().sort(ordenarPorPrioridadeEId),
  }));
}

/**
 * As atrasadas que a semana mostra numa FAIXA PRÓPRIA, fora da grade.
 *
 * 🔴 Elas vêm de antes do início da semana, então não caberiam em nenhuma coluna
 * — e sumir com elas ao navegar para a semana seguinte é exatamente como o
 * trabalho se perde de vista. A régua de atraso é a mesma da tela Hoje
 * (`grupoDaOperacao`), não uma segunda definição.
 *
 * Só aparecem quando a semana visível TERMINA em hoje ou depois — ou seja, na
 * semana corrente e nas futuras. Numa semana PASSADA a faixa some: ali
 * "atrasada em relação a hoje" não é a pergunta que se está fazendo, e o que
 * interessa é o que aconteceu naqueles dias.
 */
export function atrasadasDaSemana<T extends OperacaoOrdenavel>(
  operacoes: T[],
  inicioDaSemana: string,
  hoje: string,
): T[] {
  // A régua é o FIM da semana visível, não o início: navegando para a semana
  // que vem, a atrasada tem que continuar visível (é justamente aí que ela se
  // perderia de vista).
  if (somarDiasCivis(inicioDaSemana, 6) < hoje) return [];
  return operacoes
    .filter(op => op.dataPrevista !== null && op.dataPrevista < inicioDaSemana)
    .filter(op => grupoDaOperacao(op, hoje) === "atrasada")
    .sort(ordenarPorPrioridadeEId);
}

function ordenarPorPrioridadeEId<T extends OperacaoOrdenavel>(a: T, b: T): number {
  const p = (PESO_PRIORIDADE[a.prioridade] ?? 1) - (PESO_PRIORIDADE[b.prioridade] ?? 1);
  if (p !== 0) return p;
  const d = (a.dataPrevista ?? "").localeCompare(b.dataPrevista ?? "");
  if (d !== 0) return d;
  return a.id.localeCompare(b.id);
}

// ─── Backlog ──────────────────────────────────────────────────────────────────

export interface OperacaoBacklog extends OperacaoOrdenavel {
  createdAt?: Date | string | null;
}

/**
 * O backlog, na ordem da spec §6.3: **prioridade e depois IDADE** (mais velho
 * primeiro).
 *
 * A idade é o ponto: um item parado há três meses tem que subir sozinho, sem
 * ninguém precisar reordenar nada. Backlog que ordena por data de criação
 * decrescente (o default de toda lista) enterra justamente o que foi esquecido.
 */
export function ordenarBacklog<T extends OperacaoBacklog>(operacoes: T[]): T[] {
  return operacoes.slice().sort((a, b) => {
    const p = (PESO_PRIORIDADE[a.prioridade] ?? 1) - (PESO_PRIORIDADE[b.prioridade] ?? 1);
    if (p !== 0) return p;
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (ta !== tb) return ta - tb; // mais velho primeiro
    return a.id.localeCompare(b.id);
  });
}

/**
 * Há quantos dias o item está no backlog. `null` quando não há `createdAt` —
 * melhor não dizer nada do que dizer "há 0 dias" para um dado que falta.
 */
export function diasNoBacklog(createdAt: Date | string | null | undefined, agora: Date): number | null {
  if (!createdAt) return null;
  const t = new Date(createdAt).getTime();
  if (isNaN(t)) return null;
  return Math.max(0, Math.floor((agora.getTime() - t) / 86400000));
}

/** Filtros da guia Backlog (spec §6.3). Ambos opcionais; ausente = não filtra. */
export interface FiltroBacklog {
  categoria?: string | null;
  setorId?: string | null;
}

export interface OperacaoFiltravel {
  atividade?: { categoria?: string | null } | null;
  setores?: Array<{ setorId: string }>;
}

export function filtrarBacklog<T extends OperacaoFiltravel>(operacoes: T[], filtro: FiltroBacklog): T[] {
  return operacoes.filter(op => {
    if (filtro.categoria && (op.atividade?.categoria ?? null) !== filtro.categoria) return false;
    if (filtro.setorId && !(op.setores ?? []).some(s => s.setorId === filtro.setorId)) return false;
    return true;
  });
}
